import React, {useCallback, useState, useEffect} from 'react'
import styled from 'styled-components'
import Typography from '@material-ui/core/Typography'
import {now, date, duration, inThreeDays, Duration} from 'lib/date-time'
import {useInterval} from 'lib/interval'
import {HashMap} from 'lib/list'
import {useEditMode} from 'Event/EditModeProvider'
import {withDefaults} from 'lib/object'
import {DeepRequired} from 'lib/type-utils'
import {useAttendeeVariables} from 'Event'
import {useSavedDate} from 'Event/Dashboard/editor/views/ConfigBar/PreviewConfig'
import {Ordered} from 'lib/list'
import {Font} from 'lib/FontSelect'
import {BOLD} from 'lib/ui/typography/FontStyleInput'
import CountDownTimerUnits from 'Event/Dashboard/components/CountDownTimer/CountDownTimerUnits'

export interface CountDownTimerSettings extends Ordered {
  enabled: boolean
  end: string
  background: {
    color: string
    padding: number
    opacity?: number
    fullWidth: boolean
  }
  unitBackground: {
    color: string
    opacity?: number
    radius: number
    padding: number
  }
  numberStyles: {
    color: string
    font: Font | null
    fontSize: number
  }
  labelStyles: {
    color: string
    font: Font | null
    fontSize: number
  }
  separatorStyles: {
    color: string
  }
  descriptionStyles: {
    color: string
  }

  labels: Duration & {separator: string; description: string}
}

export const createCountdown = (
  numCountdowns: number,
): CountDownTimerSettings => ({
  enabled: true,
  end: inThreeDays(),
  background: {
    color: '#FFFFFF',
    padding: 0,
    opacity: 100,
    fullWidth: false,
  },
  unitBackground: {
    color: '#FFFFFF',
    opacity: 100,
    radius: 0,
    padding: 0,
  },
  numberStyles: {
    color: '#000000',
    font: {
      family: '',
      styles: [BOLD],
      variants: [],
    },
    fontSize: 30,
  },
  labelStyles: {
    color: '#000000',
    font: null,
    fontSize: 18,
  },
  separatorStyles: {
    color: '#000000',
  },
  descriptionStyles: {
    color: '#000000',
  },
  labels: {
    days: 'Days',
    hours: 'Hours',
    minutes: 'Minutes',
    seconds: 'Seconds',
    separator: ':',
    description: '',
  },

  position: numCountdowns,
})

const countDownTimerDefaults = createCountdown(0)

export function fillCountDownTimerDefaults(
  timers: HashMap<CountDownTimerSettings>,
) {
  return Object.entries(timers).reduce((acc, [id, timer]) => {
    acc[id] = withDefaults(
      countDownTimerDefaults,
      timer,
    ) as DeepRequired<CountDownTimerSettings>
    return acc
  }, {} as DeepRequired<HashMap<CountDownTimerSettings>>)
}

type CountDownTimerProps = CountDownTimerSettings & {
  id: string
  onRender?: () => void
  onHidden?: () => void
}

export default function CountDownTimer(props: CountDownTimerProps) {
  const [showing, setShowing] = useState(true)
  const [timeLeft, setTimeLeft] = useState<Duration>({
    hours: '00',
    minutes: '00',
    seconds: '00',
  })
  const [hasInitialized, setHasInitialized] = useState<boolean>(false)
  const isEditMode = useEditMode()
  const v = useAttendeeVariables()
  const previewDate = useSavedDate()

  const {end, enabled, onRender, onHidden} = props

  const checkHidden = () => {
    if (isEditMode) {
      return false
    }
    return !showing || !enabled
  }

  const update = useCallback(() => {
    const current = now()
    const isAfter = date(end).isAfter(previewDate.date ?? current)

    setShowing(isAfter)

    if (!isEditMode && (!showing || !enabled || !end)) {
      return
    }

    const diff = duration(current, end)

    setTimeLeft(diff)
  }, [end, previewDate, isEditMode, showing, enabled])

  useInterval(update, 1000)

  const isHidden = checkHidden()

  useEffect(() => {
    if (isHidden) {
      onHidden && onHidden()
      return
    }

    if (!Object.values(timeLeft).every((v) => v === '00')) {
      setHasInitialized(true)
    }

    onRender && onRender()
  }, [isHidden, onRender, onHidden, timeLeft])

  const current = now()
  const isLive = date(end).isAfter(current)

  if (isHidden) {
    return null
  }

  if (!hasInitialized && isLive) {
    return null
  }

  const hasDescription = Boolean(v(props.labels.description))

  return (
    <StyledCountDownTimer
      {...props}
      aria-label="count down timer"
      className={'full-width-draggable-overlay'}
    >
      <Box {...props}>
        <CountDownTimerUnits {...props} timeLeft={timeLeft} />

        {hasDescription && (
          <TypographyDesc {...props}>
            {v(props.labels.description)}
          </TypographyDesc>
        )}
      </Box>
    </StyledCountDownTimer>
  )
}

const Box = styled.div<{
  background: CountDownTimerSettings['background']
}>`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  isolation: isolate;
  overflow: hidden;

  color: ${(props) => props.color};

  padding: ${(props) => props.background.padding}px;
  width: ${(props) => (props.background.fullWidth ? '100%' : 'fit-content')};

  max-width: 100%;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background: ${(props) => props.background.color};
    background-position: center;
    background-size: cover;
    opacity: ${(props) => (props.background.opacity ?? 100) / 100};
  }
`

const StyledCountDownTimer = styled.div<{
  background: CountDownTimerSettings['background']
}>`
  box-sizing: border-box;
  margin: 0 auto;

  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;

  container-type: inline-size;
  width: 100%;
  font-size: clamp(16px, 4cqw, 36px);
`

const TypographyDesc = styled(Typography)<{
  descriptionStyles: CountDownTimerSettings['descriptionStyles']
}>`
  text-align: center;
  font-weight: inherit;
  color: ${(props) => props.descriptionStyles.color};
  margin-top: 1rem;
`
